import React, { useEffect, useRef, useState } from "react";
import {
  ArrowRight, ArrowUpRight, Check, FileText, FileCheck2, Shield,
  AlertCircle, Clock, MessageSquare, GitBranch, Users, Sparkles,
  TrendingUp, Calendar, Home, ChevronRight, Camera, MapPin,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────
   BRAND TOKENS — sourced from the ChainFlow logo
   ───────────────────────────────────────────────────────── */
const BRAND = {
  blue:   "#4F6BFF",
  indigo: "#3B4BFB",
  purple: "#4338CA",
  gradient: "linear-gradient(135deg, #4F6BFF 0%, #3B4BFB 55%, #4338CA 100%)",
};

/* ─────────────────────────────────────────────────────────
   useInView — lightweight scroll-reveal hook
   ───────────────────────────────────────────────────────── */
const useInView = (rootMargin = "-12% 0px") => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return undefined;
    const el = ref.current;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin, threshold: 0.05 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin]);
  return [ref, inView];
};

/* ─────────────────────────────────────────────────────────
   useScrollParallax — subtle Y offset based on element's
   distance from viewport center. Disabled on prefers-reduced-motion.
   ───────────────────────────────────────────────────────── */
const useScrollParallax = (strength = 24) => {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !ref.current) return undefined;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const vh = window.innerHeight || 1;
        // -1 (way below) → 0 (centered) → +1 (way above)
        const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
        const clamped = Math.max(-1, Math.min(1, progress));
        setOffset(-clamped * strength);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [strength]);
  return [ref, offset];
};

/* ─────────────────────────────────────────────────────────
   FeatureBlock — alternating premium reveal
   ───────────────────────────────────────────────────────── */
const FeatureBlock = ({
  index,
  eyebrow,
  headline,
  description,
  bullets = [],
  cta,
  layout = "right", // 'right' = text left, UI right · 'left' = text right, UI left
  children,
  testid,
}) => {
  const [ref, inView] = useInView();
  const [parallaxRef, parallaxY] = useScrollParallax(20);
  const textOrder = layout === "right" ? "lg:order-1" : "lg:order-2";
  const uiOrder   = layout === "right" ? "lg:order-2" : "lg:order-1";

  return (
    <div
      ref={ref}
      className="relative max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28"
      data-testid={testid}
    >
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] gap-12 lg:gap-20 items-center">
        {/* TEXT */}
        <div
          className={`${textOrder} ${inView ? "cf-fade-up" : "opacity-0"}`}
          style={{ animationDelay: "0ms" }}
        >
          <div className="inline-flex items-center gap-2 mb-5">
            <span
              className="text-[10.5px] font-bold uppercase tracking-[0.22em] tabular-nums"
              style={{
                background: BRAND.gradient,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {String(index).padStart(2, "0")} · {eyebrow}
            </span>
          </div>
          <h3
            className="font-display font-bold tracking-[-0.025em] text-slate-900 leading-[1.05]"
            style={{ fontSize: "clamp(30px, 3.6vw, 46px)" }}
          >
            {headline}
          </h3>
          <p className="mt-5 text-[16px] lg:text-[17px] text-slate-600 leading-relaxed max-w-[520px]">
            {description}
          </p>

          {bullets.length > 0 && (
            <ul className="mt-7 space-y-2.5">
              {bullets.map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-[14px] text-slate-700"
                >
                  <span
                    className="mt-0.5 w-4 h-4 rounded-full flex items-center justify-center text-white shrink-0"
                    style={{ background: BRAND.gradient }}
                  >
                    <Check size={10} strokeWidth={3} />
                  </span>
                  <span className="leading-snug">{b}</span>
                </li>
              ))}
            </ul>
          )}

          {cta && (
            <a
              href="#"
              className="group inline-flex items-center gap-1.5 mt-8 text-[14px] font-bold text-slate-900 hover:text-indigo-700 transition-colors"
            >
              {cta}
              <ArrowUpRight
                size={15}
                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
          )}
        </div>

        {/* UI PREVIEW with subtle scroll parallax */}
        <div
          ref={parallaxRef}
          className={`${uiOrder} ${inView ? "cf-fade-up" : "opacity-0"} will-change-transform`}
          style={{
            animationDelay: "120ms",
            transform: `translate3d(0, ${parallaxY}px, 0)`,
            transition: "transform 80ms linear",
          }}
        >
          <FeatureCanvas>{children}</FeatureCanvas>
        </div>
      </div>
    </div>
  );
};

/* Canvas: ambient halo + soft tile that hosts every preview */
const FeatureCanvas = ({ children }) => (
  <div className="relative">
    <div
      aria-hidden="true"
      className="absolute -inset-10 -z-10 pointer-events-none"
      style={{
        background:
          "radial-gradient(ellipse at center, rgba(67, 56, 202,0.10) 0%, rgba(79,107,255,0.05) 35%, transparent 70%)",
        filter: "blur(20px)",
      }}
    />
    <div className="relative">{children}</div>
  </div>
);

/* ─────────────────────────────────────────────────────────
   1 · DEAL ROOM
   ───────────────────────────────────────────────────────── */
const DealRoomPreview = () => (
  <div
    className="relative rounded-[20px] bg-white border border-slate-200/80 overflow-hidden"
    style={{
      boxShadow:
        "0 50px 100px -30px rgba(15,23,42,0.30), 0 12px 30px -10px rgba(15,23,42,0.10)",
    }}
  >
    {/* Header strip */}
    <div className="px-6 py-5 border-b border-slate-100 flex items-start gap-4">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: "linear-gradient(135deg, #DBEAFE, #E9D5FF)" }}>
        <Home size={20} className="text-indigo-700" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          Active Deal Room
        </p>
        <p className="font-display text-[18px] font-bold text-slate-900 tracking-tight mt-0.5 truncate">
          27 Tudor Street, London EC4
        </p>
        <p className="text-[12px] text-slate-500 mt-0.5">
          Stage · Searches · Day 38 · Target exchange Mar 14
        </p>
      </div>
      {/* Health pill */}
      <div className="flex items-center gap-2 h-7 px-2.5 rounded-full bg-emerald-50 border border-emerald-200">
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 cf-pulse" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
        </span>
        <span className="text-[10.5px] font-bold text-emerald-700">Healthy · 86</span>
      </div>
    </div>

    {/* Participants */}
    <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-4">
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-500">
        In the room
      </p>
      <div className="flex -space-x-2">
        {[
          { i: "JC", g: "linear-gradient(135deg,#4F6BFF,#3B4BFB)" },
          { i: "EW", g: "linear-gradient(135deg,#3B4BFB,#4338CA)" },
          { i: "DR", g: "linear-gradient(135deg,#4338CA,#4F6BFF)" },
          { i: "SH", g: "linear-gradient(135deg,#10B981,#059669)" },
          { i: "PT", g: "linear-gradient(135deg,#F59E0B,#D97706)" },
        ].map((p) => (
          <span
            key={p.i}
            className="w-7 h-7 rounded-full text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white"
            style={{ background: p.g }}
          >
            {p.i}
          </span>
        ))}
      </div>
      <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-slate-500 font-semibold">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> 2 active now
      </span>
    </div>

    {/* Critical action card */}
    <div className="p-6">
      <div
        className="relative rounded-2xl p-5 border"
        style={{
          background: "linear-gradient(180deg, #FFF1F2 0%, #FFFFFF 100%)",
          borderColor: "rgba(244,63,94,0.25)",
        }}
      >
        <div className="absolute left-0 top-3 bottom-3 w-1 rounded-r-full bg-rose-500" />
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
            <AlertCircle size={16} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-rose-700">
              Critical · Blocking exchange
            </p>
            <p className="font-display text-[17px] font-bold text-slate-900 mt-0.5">
              Awaiting buyer's mortgage offer
            </p>
            <p className="text-[12.5px] text-slate-600 mt-1 leading-relaxed">
              Last update 2 days ago · Owner: Stephen Holt (Broker)
            </p>
          </div>
          <button
            className="hidden md:inline-flex h-9 px-3.5 rounded-lg text-white text-[12px] font-bold items-center gap-1.5 transition-transform hover:-translate-y-0.5"
            style={{ background: BRAND.gradient }}
          >
            Chase <ArrowRight size={12} />
          </button>
        </div>
      </div>

      {/* Mini activity */}
      <div className="mt-4 grid grid-cols-3 gap-2.5">
        {[
          { Icon: FileCheck2, label: "Memo issued",     when: "2h ago", tone: "violet" },
          { Icon: Shield,     label: "AML cleared",     when: "1d ago", tone: "emerald" },
          { Icon: MessageSquare, label: "Solicitor msg", when: "4h ago", tone: "blue" },
        ].map((e) => (
          <div
            key={e.label}
            className="rounded-xl border border-slate-200 bg-white p-3"
          >
            <div className="flex items-center gap-2">
              <span
                className={`w-6 h-6 rounded-md flex items-center justify-center ${
                  e.tone === "violet"
                    ? "bg-indigo-100 text-indigo-700"
                    : e.tone === "emerald"
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-blue-100 text-blue-700"
                }`}
              >
                <e.Icon size={11} />
              </span>
              <p className="text-[11px] font-semibold text-slate-800 truncate">
                {e.label}
              </p>
            </div>
            <p className="text-[10px] text-slate-500 mt-1">{e.when}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────
   2 · SALES PROGRESSION TIMELINE
   ───────────────────────────────────────────────────────── */
const ProgressionPreview = () => {
  const stages = [
    { name: "Offer Accepted",     state: "done",     when: "Jan 12" },
    { name: "AML & ID",           state: "done",     when: "Jan 18" },
    { name: "Memorandum issued",  state: "done",     when: "Jan 22" },
    { name: "Mortgage offer",     state: "current",  when: "In progress" },
    { name: "Searches",           state: "upcoming", when: "Est. Feb 18" },
    { name: "Enquiries",          state: "upcoming", when: "Est. Feb 26" },
    { name: "Exchange contracts", state: "upcoming", when: "Est. Mar 14" },
  ];
  return (
    <div
      className="relative rounded-[20px] bg-white border border-slate-200/80 overflow-hidden p-6"
      style={{
        boxShadow:
          "0 50px 100px -30px rgba(15,23,42,0.30), 0 12px 30px -10px rgba(15,23,42,0.10)",
      }}
    >
      <div className="flex items-center justify-between mb-5">
        <p className="font-display text-[15px] font-bold text-slate-900 tracking-tight">
          Sales Progression
        </p>
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          43% complete
        </span>
      </div>

      {/* Vertical timeline */}
      <ol className="relative pl-6">
        <span
          aria-hidden="true"
          className="absolute left-[7px] top-1 bottom-1 w-px"
          style={{
            background:
              "linear-gradient(180deg, #3B4BFB 0%, #3B4BFB 42%, #E2E8F0 42%, #E2E8F0 100%)",
          }}
        />
        {stages.map((s, i) => (
          <li key={s.name} className="relative pb-4 last:pb-0">
            <span
              className={`absolute -left-[24px] top-0.5 flex items-center justify-center w-[14px] h-[14px] rounded-full ring-4 ${
                s.state === "done"
                  ? "ring-white"
                  : s.state === "current"
                  ? "ring-indigo-100"
                  : "ring-white"
              }`}
              style={{
                background:
                  s.state === "done"
                    ? BRAND.gradient
                    : s.state === "current"
                    ? "#FFF"
                    : "#E2E8F0",
                border:
                  s.state === "current" ? "2px solid #3B4BFB" : "none",
              }}
            >
              {s.state === "done" && <Check size={8} className="text-white" strokeWidth={3} />}
              {s.state === "current" && (
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-600 cf-pulse" />
              )}
            </span>
            <div className="flex items-center justify-between gap-3">
              <p
                className={`text-[13.5px] font-semibold ${
                  s.state === "done"
                    ? "text-slate-700"
                    : s.state === "current"
                    ? "text-slate-900"
                    : "text-slate-500"
                }`}
              >
                {s.name}
              </p>
              <p
                className={`text-[11px] font-semibold tabular-nums ${
                  s.state === "current" ? "text-indigo-700" : "text-slate-400"
                }`}
              >
                {s.when}
              </p>
            </div>
            {s.state === "current" && (
              <div className="mt-2 inline-flex items-center gap-1.5 h-6 px-2 rounded-md bg-amber-50 border border-amber-200">
                <Clock size={10} className="text-amber-600" />
                <span className="text-[10.5px] font-semibold text-amber-700">
                  Awaiting broker · 2 days
                </span>
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   3 · OFFER SCORE
   ───────────────────────────────────────────────────────── */
const OfferScorePreview = () => {
  const score = 87;
  const factors = [
    { label: "Funding verified",      v: 95, tone: "emerald" },
    { label: "Chain position",        v: 88, tone: "indigo" },
    { label: "Speed to exchange",     v: 80, tone: "indigo" },
    { label: "Buyer engagement",      v: 90, tone: "emerald" },
  ];
  const radius = 54;
  const circ = 2 * Math.PI * radius;
  const dash = (score / 100) * circ;
  return (
    <div className="relative">
      {/* Floating side card · price */}
      <div
        className="absolute -top-6 -right-6 z-10 rounded-2xl p-4 hidden md:block cf-float"
        style={{
          background: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          border: "1px solid rgba(255,255,255,0.7)",
          boxShadow:
            "0 20px 50px -20px rgba(15,23,42,0.25), 0 0 0 1px rgba(255,255,255,0.6) inset",
        }}
      >
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
          Offer
        </p>
        <p className="font-display text-[20px] font-bold tracking-tight text-slate-900 mt-0.5">
          £685,000
        </p>
        <p className="text-[10.5px] text-emerald-600 font-semibold mt-0.5">
          ▲ £35k over asking
        </p>
      </div>

      <div
        className="relative rounded-[20px] bg-white border border-slate-200/80 p-7"
        style={{
          boxShadow:
            "0 50px 100px -30px rgba(15,23,42,0.30), 0 12px 30px -10px rgba(15,23,42,0.10)",
        }}
      >
        <div className="flex items-center gap-6">
          {/* Circular gauge */}
          <div className="relative w-[140px] h-[140px] shrink-0">
            <svg viewBox="0 0 130 130" className="w-full h-full -rotate-90">
              <defs>
                <linearGradient id="cf-score-grad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#4F6BFF" />
                  <stop offset="55%" stopColor="#3B4BFB" />
                  <stop offset="100%" stopColor="#4338CA" />
                </linearGradient>
              </defs>
              <circle cx="65" cy="65" r={radius} fill="none" stroke="#E2E8F0" strokeWidth="10" />
              <circle
                cx="65" cy="65" r={radius}
                fill="none"
                stroke="url(#cf-score-grad)"
                strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={`${dash} ${circ}`}
                style={{ transition: "stroke-dasharray 1.2s ease-out" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
              <p className="font-display text-[36px] font-bold tracking-tight text-slate-900 tabular-nums">
                {score}
              </p>
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 mt-0.5">
                / 100
              </p>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              Offer Score
            </p>
            <p className="font-display text-[22px] font-bold text-slate-900 tracking-tight mt-0.5">
              Strong buyer · Recommend accept
            </p>
            <p className="text-[12.5px] text-slate-600 mt-1 leading-relaxed">
              Funding verified through Lloyds. No chain. Solicitor instructed.
            </p>
          </div>
        </div>

        {/* Factors */}
        <div className="mt-6 space-y-3">
          {factors.map((f) => (
            <div key={f.label} className="flex items-center gap-3">
              <p className="text-[12px] font-semibold text-slate-700 w-[160px] shrink-0">
                {f.label}
              </p>
              <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${f.v}%`,
                    background:
                      f.tone === "emerald"
                        ? "linear-gradient(90deg,#10B981,#059669)"
                        : BRAND.gradient,
                    transition: "width 1.2s ease-out",
                  }}
                />
              </div>
              <p className="text-[12px] font-bold text-slate-900 tabular-nums w-8 text-right">
                {f.v}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   4 · DOCUMENT HUB
   ───────────────────────────────────────────────────────── */
const DocumentHubPreview = () => {
  const docs = [
    { name: "Memorandum of Sale.pdf",  size: "184 KB",   status: "verified", uploader: "JC", color: "violet" },
    { name: "Mortgage Offer.pdf",      size: "1.2 MB",   status: "verified", uploader: "SH", color: "violet" },
    { name: "AML Verification.pdf",    size: "62 KB",    status: "verified", uploader: "EW", color: "emerald" },
    { name: "Search Results.zip",      size: "8.4 MB",   status: "pending",  uploader: "DR", color: "amber" },
    { name: "Contract Pack.pdf",       size: "—",        status: "awaiting", uploader: "—",  color: "slate" },
  ];
  return (
    <div className="relative">
      <div
        className="rounded-[20px] bg-white border border-slate-200/80 overflow-hidden"
        style={{
          boxShadow:
            "0 50px 100px -30px rgba(15,23,42,0.30), 0 12px 30px -10px rgba(15,23,42,0.10)",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
          <div>
            <p className="font-display text-[15px] font-bold text-slate-900 tracking-tight">
              Conveyancing pack
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Encrypted · Signed URLs · 1h expiry
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-emerald-50 border border-emerald-200">
            <Shield size={11} className="text-emerald-700" />
            <span className="text-[10.5px] font-bold text-emerald-700">Secure</span>
          </span>
        </div>

        {/* Doc list */}
        <ul className="divide-y divide-slate-100">
          {docs.map((d) => (
            <li
              key={d.name}
              className="group flex items-center gap-3 px-6 py-3.5 hover:bg-slate-50 transition-colors"
            >
              <span
                className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                  d.color === "violet"
                    ? "bg-indigo-100 text-indigo-700"
                    : d.color === "emerald"
                    ? "bg-emerald-100 text-emerald-700"
                    : d.color === "amber"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-slate-100 text-slate-500"
                }`}
              >
                <FileText size={15} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-slate-900 truncate">
                  {d.name}
                </p>
                <p className="text-[10.5px] text-slate-500 mt-0.5">{d.size}</p>
              </div>
              {/* Status pill */}
              <span
                className={`hidden sm:inline-flex items-center gap-1 h-6 px-2 rounded-md text-[10.5px] font-bold ${
                  d.status === "verified"
                    ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
                    : d.status === "pending"
                    ? "bg-amber-50 border border-amber-200 text-amber-700"
                    : "bg-slate-50 border border-slate-200 text-slate-500"
                }`}
              >
                {d.status === "verified" && <Check size={10} strokeWidth={3} />}
                {d.status === "pending" && <Clock size={10} />}
                {d.status === "awaiting" && <AlertCircle size={10} />}
                <span className="capitalize">{d.status}</span>
              </span>
              <span
                className="w-7 h-7 rounded-full text-white text-[10px] font-bold flex items-center justify-center shrink-0"
                style={{ background: BRAND.gradient, opacity: d.uploader === "—" ? 0 : 1 }}
              >
                {d.uploader}
              </span>
            </li>
          ))}
        </ul>

        {/* Drop zone */}
        <div className="m-5 mt-3 rounded-xl border-2 border-dashed border-slate-200 px-5 py-4 text-center">
          <p className="text-[12px] font-semibold text-slate-700">
            Drop files here or
            <span
              className="ml-1"
              style={{
                background: BRAND.gradient,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              browse
            </span>
          </p>
          <p className="text-[10.5px] text-slate-500 mt-0.5">
            PDF · DOC · XLS · 25MB max
          </p>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   5 · CHAIN VIEW
   ───────────────────────────────────────────────────────── */
const ChainViewPreview = () => {
  const links = [
    { label: "Buyer",       sub: "First-time",     state: "done",      price: "£420k" },
    { label: "Your sale",   sub: "27 Tudor St",    state: "current",   price: "£685k" },
    { label: "Onward",      sub: "10 High St",     state: "blocked",   price: "£820k" },
    { label: "Top",         sub: "8 Heritage Mews", state: "upcoming", price: "£1.2M" },
  ];
  return (
    <div
      className="relative rounded-[20px] bg-white border border-slate-200/80 overflow-hidden p-6"
      style={{
        boxShadow:
          "0 50px 100px -30px rgba(15,23,42,0.30), 0 12px 30px -10px rgba(15,23,42,0.10)",
      }}
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="font-display text-[15px] font-bold text-slate-900 tracking-tight">
            Chain view
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            4 properties · 1 bottleneck detected
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-rose-50 border border-rose-200">
          <AlertCircle size={11} className="text-rose-600" />
          <span className="text-[10.5px] font-bold text-rose-700">Bottleneck</span>
        </span>
      </div>

      {/* Chain row */}
      <div className="relative">
        <div className="grid grid-cols-4 gap-3 relative z-10">
          {links.map((l, i) => (
            <div
              key={l.label}
              className={`relative rounded-xl border p-3 transition-transform ${
                l.state === "current"
                  ? "border-indigo-300 bg-gradient-to-br from-indigo-50/60 to-white"
                  : l.state === "blocked"
                  ? "border-rose-300 bg-gradient-to-br from-rose-50/60 to-white"
                  : "border-slate-200 bg-white"
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`w-7 h-7 rounded-lg flex items-center justify-center text-white shrink-0`}
                  style={{
                    background:
                      l.state === "blocked"
                        ? "linear-gradient(135deg,#FB7185,#E11D48)"
                        : l.state === "done"
                        ? "linear-gradient(135deg,#10B981,#059669)"
                        : BRAND.gradient,
                  }}
                >
                  <Home size={12} />
                </span>
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 truncate">
                  {l.label}
                </p>
              </div>
              <p className="text-[12.5px] font-bold text-slate-900 mt-2 truncate">
                {l.sub}
              </p>
              <p
                className={`text-[10.5px] font-semibold mt-1 tabular-nums ${
                  l.state === "blocked" ? "text-rose-600" : "text-slate-500"
                }`}
              >
                {l.price}
              </p>
              {l.state === "blocked" && (
                <p className="text-[10px] font-semibold text-rose-600 mt-1.5 inline-flex items-center gap-1">
                  <AlertCircle size={9} /> Solicitor inactive 4d
                </p>
              )}
              {l.state === "current" && (
                <p className="text-[10px] font-semibold text-indigo-700 mt-1.5 inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-600 cf-pulse" /> You
                </p>
              )}

              {/* Connector arrow */}
              {i < links.length - 1 && (
                <span className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 z-20">
                  <ChevronRight
                    size={16}
                    className={
                      l.state === "blocked" || links[i + 1].state === "blocked"
                        ? "text-rose-400"
                        : "text-slate-300"
                    }
                  />
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Footer recommendation */}
      <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 flex items-start gap-2.5">
        <Sparkles size={13} className="text-indigo-600 mt-0.5" />
        <p className="text-[11.5px] text-slate-700 leading-snug">
          <span className="font-bold text-slate-900">Recommendation:</span> Chase
          solicitor at 10 High St — completion at risk if not actioned within 48h.
        </p>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   6 · DIRECTOR DASHBOARD
   ───────────────────────────────────────────────────────── */
const DirectorDashboardPreview = () => {
  const branches = [
    { name: "Mayfair",      val: 4.8, deals: 28 },
    { name: "Hampstead",    val: 3.2, deals: 21 },
    { name: "Stratford",    val: 2.6, deals: 17 },
    { name: "Canary Wharf", val: 1.9, deals: 12 },
  ];
  const max = Math.max(...branches.map((b) => b.val));
  return (
    <div
      className="relative rounded-[20px] bg-white border border-slate-200/80 overflow-hidden"
      style={{
        boxShadow:
          "0 50px 100px -30px rgba(15,23,42,0.30), 0 12px 30px -10px rgba(15,23,42,0.10)",
      }}
    >
      {/* Top stat strip */}
      <div className="grid grid-cols-3 border-b border-slate-100">
        {[
          { label: "Group pipeline", value: "£28.4M", sub: "▲ 22% YoY" },
          { label: "Deals tracked",  value: "164",    sub: "5 branches" },
          { label: "Forecast Q1",    value: "£9.7M",  sub: "▲ vs target £8.4M" },
        ].map((s, i) => (
          <div
            key={s.label}
            className={`px-5 py-4 ${i < 2 ? "border-r border-slate-100" : ""}`}
          >
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              {s.label}
            </p>
            <p className="font-display text-[20px] font-bold tracking-tight text-slate-900 mt-1 tabular-nums">
              {s.value}
            </p>
            <p className="text-[10.5px] text-emerald-600 font-semibold mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Branch leaderboard */}
      <div className="px-6 py-5">
        <div className="flex items-center justify-between mb-3">
          <p className="font-display text-[14px] font-bold text-slate-900 tracking-tight">
            Branch performance · pipeline £M
          </p>
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            Last 30 days
          </span>
        </div>
        <ul className="space-y-2.5">
          {branches.map((b, i) => (
            <li key={b.name} className="flex items-center gap-3">
              <span className="text-[11px] font-bold text-slate-400 w-4 tabular-nums">
                {i + 1}
              </span>
              <p className="text-[12.5px] font-semibold text-slate-800 w-[110px] truncate">
                {b.name}
              </p>
              <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${(b.val / max) * 100}%`,
                    background: BRAND.gradient,
                    transition: "width 1.2s ease-out",
                  }}
                />
              </div>
              <p className="text-[12px] font-bold text-slate-900 tabular-nums w-12 text-right">
                £{b.val}M
              </p>
              <p className="hidden sm:block text-[10.5px] text-slate-500 w-12 text-right tabular-nums">
                {b.deals} deals
              </p>
            </li>
          ))}
        </ul>
      </div>

      {/* Bottom: top performers + risk */}
      <div className="grid grid-cols-2 border-t border-slate-100">
        <div className="px-6 py-4 border-r border-slate-100">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            Top performer
          </p>
          <div className="flex items-center gap-2.5 mt-2">
            <span
              className="w-9 h-9 rounded-full text-white text-[11px] font-bold flex items-center justify-center"
              style={{ background: BRAND.gradient }}
            >
              JC
            </span>
            <div>
              <p className="text-[13px] font-bold text-slate-900">James Carter</p>
              <p className="text-[10.5px] text-slate-500">
                12 deals · £4.2M progressed
              </p>
            </div>
          </div>
        </div>
        <div className="px-6 py-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            At risk fees
          </p>
          <div className="flex items-baseline gap-2 mt-2">
            <p className="font-display text-[18px] font-bold tracking-tight text-rose-700 tabular-nums">
              £148,500
            </p>
            <p className="text-[10.5px] text-rose-600 font-semibold">
              4 deals
            </p>
          </div>
          <p className="text-[10.5px] text-slate-500 mt-1">
            View at risk →
          </p>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   7 · SELLER LAUNCH HUB
   ───────────────────────────────────────────────────────── */
const SellerLaunchPreview = () => {
  const items = [
    { Icon: Camera,    label: "Photography booked",   when: "Tue · 10:00", state: "done" },
    { Icon: MapPin,    label: "Floor plan uploaded",  when: "Yesterday",   state: "done" },
    { Icon: FileText,  label: "EPC certificate",      when: "Pending",     state: "current" },
    { Icon: Users,     label: "Solicitor instructed", when: "Awaiting",    state: "upcoming" },
    { Icon: TrendingUp,label: "Listing live",         when: "Est. Fri",    state: "upcoming" },
  ];
  const done = items.filter((i) => i.state === "done").length;
  const pct = Math.round((done / items.length) * 100);
  return (
    <div className="relative flex justify-center">
      {/* iPhone frame */}
      <div
        className="relative rounded-[44px] p-2"
        style={{
          background: "linear-gradient(180deg, #1F2937 0%, #0F172A 100%)",
          boxShadow:
            "0 50px 100px -30px rgba(15,23,42,0.50), 0 12px 30px -10px rgba(15,23,42,0.20), 0 0 0 1px rgba(15,23,42,0.4) inset",
          width: "340px",
          maxWidth: "100%",
        }}
      >
        {/* Notch */}
        <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-24 h-5 rounded-full bg-slate-900 z-10" />
        <div className="rounded-[36px] bg-white overflow-hidden">
          {/* Top bar */}
          <div className="flex items-center justify-between px-5 pt-7 pb-3">
            <span className="text-[10px] font-bold text-slate-900">9:41</span>
            <span className="text-[10px] font-bold text-slate-900">●●●●● 5G</span>
          </div>
          {/* Header card */}
          <div className="px-5 pt-2 pb-4">
            <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              Welcome back, Eleanor
            </p>
            <p className="font-display text-[19px] font-bold text-slate-900 tracking-tight mt-0.5">
              Your launch checklist
            </p>
            {/* Progress */}
            <div className="mt-3 flex items-center gap-2.5">
              <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${pct}%`,
                    background: BRAND.gradient,
                    transition: "width 1.2s ease-out",
                  }}
                />
              </div>
              <p className="text-[11px] font-bold text-slate-900 tabular-nums">{pct}%</p>
            </div>
          </div>
          {/* Items */}
          <ul className="px-3 pb-5 space-y-1.5">
            {items.map((it) => (
              <li
                key={it.label}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${
                  it.state === "current"
                    ? "border-indigo-200 bg-indigo-50/40"
                    : "border-slate-100 bg-white"
                }`}
              >
                <span
                  className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    it.state === "done"
                      ? "text-white"
                      : it.state === "current"
                      ? "bg-white border border-indigo-300 text-indigo-700"
                      : "bg-slate-100 text-slate-400"
                  }`}
                  style={
                    it.state === "done"
                      ? { background: BRAND.gradient }
                      : undefined
                  }
                >
                  {it.state === "done" ? (
                    <Check size={13} strokeWidth={3} />
                  ) : (
                    <it.Icon size={13} />
                  )}
                </span>
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-[12px] font-semibold ${
                      it.state === "done" ? "text-slate-500 line-through" : "text-slate-900"
                    }`}
                  >
                    {it.label}
                  </p>
                  <p className="text-[10px] text-slate-500 mt-0.5">{it.when}</p>
                </div>
                {it.state === "current" && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-indigo-700">
                    Action <ChevronRight size={10} />
                  </span>
                )}
              </li>
            ))}
          </ul>
          {/* Bottom CTA */}
          <div className="px-3 pb-4">
            <button
              className="w-full h-11 rounded-xl text-white text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5"
              style={{
                background: BRAND.gradient,
                boxShadow: "0 12px 28px -10px rgba(67, 56, 202,0.50)",
              }}
            >
              Continue setup <ArrowRight size={13} />
            </button>
            <div className="h-1 w-32 mx-auto mt-3 rounded-full bg-slate-300" />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   FEATURE SEQUENCE — 7 alternating reveals
   ───────────────────────────────────────────────────────── */
const FeatureSequenceSection = () => {
  return (
    <section
      className="relative"
      data-testid="feature-sequence"
    >
      {/* Section opener */}
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-2 text-center">
        <p
          className="text-[11px] font-bold uppercase tracking-[0.24em] cf-fade-up"
          style={{
            background: BRAND.gradient,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          The Platform
        </p>
        <h2
          className="font-display font-bold tracking-[-0.035em] text-slate-900 mt-3 leading-[1.02] cf-fade-up"
          style={{ fontSize: "clamp(36px, 5vw, 64px)", animationDelay: "100ms" }}
        >
          Seven surfaces.
          <br />
          One operating system.
        </h2>
        <p
          className="mt-5 text-[16px] lg:text-[18px] text-slate-600 max-w-[640px] mx-auto leading-relaxed cf-fade-up"
          style={{ animationDelay: "180ms" }}
        >
          Each one designed to remove a specific friction point in the
          property transaction. Together they replace every spreadsheet,
          email thread, and group chat in your business.
        </p>
      </div>

      <FeatureBlock
        index={1}
        eyebrow="Deal Room"
        headline="A command center for every transaction."
        description="Property, participants, progression, and the next critical action—all in one calm, focused workspace. No more forwarding emails to find the latest update."
        bullets={[
          "Live presence · see who's active in the room",
          "Property-context messaging · no thread loss",
          "Auto-surfaced blockers · one click to chase",
        ]}
        cta="Tour the Deal Room"
        layout="right"
        testid="feature-deal-room"
      >
        <DealRoomPreview />
      </FeatureBlock>

      <FeatureBlock
        index={2}
        eyebrow="Sales Progression"
        headline="Every stage. Every owner. Every ETA."
        description="A chronological source of truth for the entire deal. Stages auto-advance from real evidence—uploaded documents, completed tasks, broker confirmations—not status fields."
        bullets={[
          "12 canonical UK conveyancing stages",
          "Auto-advance on real signal · not manual ticks",
          "Estimated dates with daily drift forecast",
        ]}
        cta="See progression in action"
        layout="left"
        testid="feature-progression"
      >
        <ProgressionPreview />
      </FeatureBlock>

      <FeatureBlock
        index={3}
        eyebrow="Offer Score"
        headline="Know which buyer to trust—instantly."
        description="A weighted score for every offer based on funding, chain position, speed, and engagement signals. Stop accepting offers that fall through three weeks later."
        bullets={[
          "Bank-verified funding signals",
          "Chain-position risk modelling",
          "Engagement decay tracking",
        ]}
        cta="See the model"
        layout="right"
        testid="feature-offer-score"
      >
        <OfferScorePreview />
      </FeatureBlock>

      <FeatureBlock
        index={4}
        eyebrow="Document Hub"
        headline="Encrypted vault. Signed access. Zero friction."
        description="Every document tied to its deal. Time-limited signed URLs replace email attachments. Audit trail for compliance, drag-drop simplicity for users."
        bullets={[
          "1-hour signed URLs · no permanent leak surface",
          "25 MB max · PDF/DOC/XLS/CSV/IMG/ZIP",
          "Verified · Pending · Awaiting status pills",
        ]}
        cta="Read the security brief"
        layout="left"
        testid="feature-document-hub"
      >
        <DocumentHubPreview />
      </FeatureBlock>

      <FeatureBlock
        index={5}
        eyebrow="Chain View"
        headline="See the entire chain in one frame."
        description="Map every dependent property in the chain. Detect bottlenecks before they become fall-throughs. Built for the way UK property actually transacts."
        bullets={[
          "Multi-property dependency mapping",
          "Automatic bottleneck detection",
          "Recommendation engine for where to push",
        ]}
        cta="Map a chain"
        layout="right"
        testid="feature-chain-view"
      >
        <ChainViewPreview />
      </FeatureBlock>

      <FeatureBlock
        index={6}
        eyebrow="Director Dashboard"
        headline="The whole business, on one screen."
        description="Group pipeline, branch leaderboards, top performers, fees at risk—aggregated live across every Deal Room. Designed for the 7am espresso."
        bullets={[
          "Multi-branch roll-up · live, no daily exports",
          "Leaderboards by negotiator and branch",
          "At-risk fees with one-click drill-down",
        ]}
        cta="See the executive view"
        layout="left"
        testid="feature-director-dashboard"
      >
        <DirectorDashboardPreview />
      </FeatureBlock>

      <FeatureBlock
        index={7}
        eyebrow="Seller Launch Hub"
        headline="Onboard sellers in minutes—not weeks."
        description="A guided checklist that walks every seller through photography, EPC, solicitor instruction, and listing approval. Branded as your agency. Mobile-first."
        bullets={[
          "Branded white-label seller portal",
          "Auto-reminders · no chase calls",
          "Instructed-to-listed in under 5 days",
        ]}
        cta="Preview the seller flow"
        layout="right"
        testid="feature-seller-launch"
      >
        <SellerLaunchPreview />
      </FeatureBlock>
    </section>
  );
};

export default FeatureSequenceSection;
